/**
 * Payment Service for My!hu Application
 * Supports Paddle payment processing
 */

import { subscriptionService } from "./subscriptionService";

export interface PaymentProvider {
  id: "paystack";
  name: string;
  region: "GLOBAL" | "INTERNATIONAL";
  currencies: string[];
  logo: string;
  description: string;
}

export interface PaymentSession {
  sessionId: string;
  amount: number;
  currency: string;
  planId: string;
  provider: PaymentProvider["id"];
  redirectUrl?: string;
  accessCode?: string; // Paystack Popup inline JS - preferred over full-page redirect
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: "month" | "year";
  features: string[];
}

// Payment providers configuration
export const PAYMENT_PROVIDERS: PaymentProvider[] = [
  {
    id: "paddle",
    name: "Paddle",
    region: "GLOBAL",
    currencies: ["USD", "EUR", "GBP", "ZAR", "CAD", "AUD"],
    logo: "/payment-logos/paddle.svg",
    description:
      "Fast and secure payments with Paddle. All-in-one platform for billing and revenue operations.",
  },
  {
    id: "paystack",
    name: "Paystack",
    region: "INTERNATIONAL",
    currencies: ["NGN", "USD", "GHS", "ZAR", "KES"],
    logo: "/payment-logos/paystack.svg",
    description:
      "Simple, secure payments for online and offline businesses in Africa.",
  },
];

class PaymentService {
  private detectUserRegion(): string {
    // Try to detect user's region from various sources
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language;

    // Default to international for global compatibility
    return "INTERNATIONAL";
  }

  getPaymentMethods(providerId: PaymentProvider["id"]): string[] {
    return ["Credit Card", "Debit Card", "Bank Transfer", "Mobile Money"];
  }

  getRecommendedProvider(currency: string = "ZAR"): PaymentProvider {
    // Return Paystack as the only option
    return PAYMENT_PROVIDERS.find((p) => p.id === "paystack")!;
  }

  async createPaymentSession(
    plan: SubscriptionPlan,
    provider?: PaymentProvider["id"],
    user?: {
      id: string;
      email: string;
      user_metadata?: { full_name?: string };
    },
  ): Promise<PaymentSession> {
    // Only Paystack is supported
    return this.createPaystackSession(plan, user);
  }

  private async createPaystackSession(
    plan: SubscriptionPlan,
    user?: any,
  ): Promise<PaymentSession> {
    try {
      // Use environment variable or default to proxy path
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/paystack-checkout`;

      // Build request body based on plan type
      let requestBody: any = {
        planId: plan.id,
        currency: plan.currency,
        userId: user?.id || "anonymous",
        userEmail: user?.email || "user@example.com",
        userName: user?.user_metadata?.full_name || "User",
        redirectUrl: `${window.location.origin}/payment/success`,
      };

      // Only include amount for credit purchases, not for subscription plans
      if (plan.id.startsWith("credits-")) {
        // For credit purchases, convert ZAR to kobo (ZAR cents) for Paystack API
        const paystackAmount = Math.round(plan.price * 100);
        requestBody.amount = paystackAmount;
        console.log(
          "Credit purchase - sending amount:",
          paystackAmount,
          "kobo",
        );
      } else if (plan.id === "professional") {
        // Paystack API requires 'amount' even for plan-based subscriptions.
        // Must be an integer in the smallest currency unit (ZAR kobo = cents × 100), no decimal places.
        const paystackAmount = Math.round(plan.price * 100);
        requestBody.amount = paystackAmount;
        console.log(
          "Professional subscription - sending amount in kobo:",
          paystackAmount,
          `(= R${plan.price.toFixed(2)})`,
        );
      }

      console.log("Creating Paystack session for plan:", plan.id);
      console.log("Plan price:", plan.price, plan.currency);
      console.log("Request URL:", apiUrl);
      console.log("Request Body:", requestBody);

      // Call your backend to create Paystack checkout
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(requestBody),
      });

      const session = await response.json();

      // Log the response for debugging
      console.log("Paystack session response:", session);
      console.log("Response status:", response.status, response.ok);

      // Check for error in response
      if (!response.ok) {
        const errorDetail = session?.detail || "Unknown error";
        throw new Error(errorDetail);
      }

      if (!session.url && !session.access_code) {
        throw new Error("Paystack checkout URL not found in response");
      }

      return {
        sessionId: session.checkoutId || session.id,
        amount: plan.price,
        currency: plan.currency,
        planId: plan.id,
        provider: "paystack",
        accessCode: session.access_code, // Popup (preferred - in-page modal)
        redirectUrl: session.url, // Fallback redirect URL
      };
    } catch (error) {
      console.error("Paystack session creation failed:", error);
      throw new Error("Failed to create Paystack payment session: " + error);
    }
  }

  private generatePayFastSignature(
    data: Record<string, string>,
    passPhrase?: string,
  ): string {
    // This method is no longer needed but kept for reference
    // In a real implementation, signature generation should be done on the backend
    console.warn("PayFast integration has been removed");
    return "signature_placeholder";
  }

  async verifyPayment(
    sessionId: string,
    provider: PaymentProvider["id"],
    userId?: string,
    planId?: string,
  ): Promise<boolean> {
    const maxRetries = 6; // Try for up to 30 seconds
    const retryDelay = 5000; // 5 seconds between retries

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-payment/${provider}/${sessionId}`;

        console.log(
          `🔍 Verifying payment (attempt ${attempt}/${maxRetries}):`,
          {
            sessionId,
            provider,
            userId,
            planId,
          },
        );
        console.log("📡 API URL:", apiUrl);

        const response = await fetch(apiUrl, {
          headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
        });

        console.log(
          "📡 Response status:",
          response.status,
          response.statusText,
        );
        console.log(
          "📡 Response headers:",
          Object.fromEntries(response.headers.entries()),
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error("❌ API response not OK:", {
            status: response.status,
            statusText: response.statusText,
            body: errorText,
            attempt,
          });

          // If it's the last attempt, return false
          if (attempt === maxRetries) {
            return false;
          }

          // Wait before retrying
          console.log(
            `⏳ Waiting ${retryDelay / 1000}s before retry ${attempt + 1}...`,
          );
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          continue;
        }

        const result = await response.json();
        console.log("✅ Payment verification result:", result);

        // If payment is verified and we have user details, process the payment
        if (result.success && userId && planId) {
          const isCredit = planId.startsWith("credits-");
          console.log(
            `💳 Processing ${isCredit ? "credit purchase" : "subscription"} for verified payment...`,
          );
          await this.processPaymentSuccess(
            sessionId,
            provider,
            userId,
            planId,
            result,
          );
        } else if (!result.success && attempt < maxRetries) {
          console.log(
            `⏳ Payment not yet processed by webhook, retrying in ${retryDelay / 1000}s... (${attempt}/${maxRetries})`,
          );
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          continue;
        } else {
          console.log("⚠️ Skipping subscription processing:", {
            verified: result.success,
            hasUserId: !!userId,
            hasPlanId: !!planId,
          });
        }

        return result.success;
      } catch (error) {
        console.error(
          `💥 Payment verification failed (attempt ${attempt}):`,
          error,
        );

        if (attempt === maxRetries) {
          return false;
        }

        // Wait before retrying
        console.log(
          `⏳ Waiting ${retryDelay / 1000}s before retry ${attempt + 1}...`,
        );
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
    }

    return false;
  }

  /**
   * Process successful payment by updating subscription and account tier
   */
  async processPaymentSuccess(
    sessionId: string,
    provider: PaymentProvider["id"],
    userId: string,
    planId: string,
    paymentResult: any,
  ): Promise<void> {
    try {
      console.log("🎯 Processing payment success:", {
        sessionId,
        provider,
        userId,
        planId,
        paymentResult,
      });

      // Extract amount and currency from payment result or use defaults based on plan
      let amount = paymentResult.amount;
      let currency = paymentResult.currency || "ZAR";

      // Fallback to plan-based pricing if not in payment result (ZAR amounts)
      if (!amount) {
        switch (planId) {
          case "professional":
            amount = 1157.97; // R1,157.97 - will be converted to kobo by frontend
            break;
          case "credits-5":
            amount = 150.0; // R150.00 - will be converted to kobo by frontend
            break;
          case "credits-10":
            amount = 135.0; // R135.00 - will be converted to kobo by frontend
            break;
          case "credits-15":
            amount = 202.5; // R202.50 - will be converted to kobo by frontend
            break;
          case "credits-20":
            amount = 270.0; // R270.00 - will be converted to kobo by frontend
            break;
          default:
            amount = 0;
        }
      }

      console.log("💰 Payment details:", { amount, currency, planId });

      const result = await subscriptionService.processSuccessfulPayment(
        userId,
        planId,
        {
          provider: provider,
          provider_transaction_id: sessionId,
          amount: amount,
          currency: currency,
          payment_details: paymentResult,
        },
      );

      if (result.success) {
        console.log("✅ Payment processed successfully:", result);
        console.log("📄 Transaction ID:", result.transaction?.id);
        console.log("📋 Subscription ID:", result.subscription?.id);
      } else {
        console.error("❌ Failed to process payment:", result.error);
        throw new Error(`Payment processing failed: ${result.error}`);
      }
    } catch (error) {
      console.error("❌ Error processing payment success:", error);
      throw error;
    }
  }

  /**
   * Manual payment processing for when verification fails but payment is confirmed
   * Use this as a last resort when other verification methods fail
   */
  async manualPaymentProcessing(
    sessionId: string,
    provider: PaymentProvider["id"],
    userId: string,
    planId: string,
    confirmedByUser: boolean = false,
  ): Promise<boolean> {
    if (!confirmedByUser) {
      console.warn("⚠️ Manual payment processing requires user confirmation");
      return false;
    }

    try {
      console.log("🔧 Starting manual payment processing...", {
        sessionId,
        provider,
        userId,
        planId,
      });

      const manualPaymentResult = {
        success: true,
        amount:
          planId === "standard" ? 19.99 : planId === "professional" ? 49.99 : 0,
        currency: "ZAR",
        payment_method: "credit_card",
        order_id: sessionId,
        status: "COMPLETED",
        timestamp: new Date().toISOString(),
        manual_processing: true,
        processed_by: "fallback_system",
      };

      await this.processPaymentSuccess(
        sessionId,
        provider,
        userId,
        planId,
        manualPaymentResult,
      );

      console.log("✅ Manual payment processing completed");
      return true;
    } catch (error) {
      console.error("💥 Manual payment processing failed:", error);
      return false;
    }
  }

  /**
   * Cancel the user's active subscription and downgrade them to PAYG.
   *
   * How Paystack cancellations work:
   * Paystack in this app uses one-time charges (not recurring subscriptions),
   * so "cancellation" is handled entirely in our own database: the backend
   * marks the active subscription as cancelled and inserts a fresh PAYG row.
   * If Paystack's recurring subscription API were used, the backend would also
   * call POST /subscription/disable — that logic is already in the endpoint.
   */
  async cancelSubscription(
    userId: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ userId }),
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.detail || "Failed to cancel subscription");
    }

    return data as { success: boolean; message: string };
  }
}

export const paymentService = new PaymentService();
